import React, { useState, useEffect } from 'react';
import { CompanyInfo, QuoteData, InvoiceItem, HistoryRecord } from './types';
import {
  loadSettings, saveSettings,
  addRecord, loadHistory, deleteRecord,
  generateDocNo, downloadBackup, importBackup,
} from './utils/storage';
import { getTodayString, formatNumber } from './utils/formatters';
import {
  downloadQuotePDF, downloadReceiptPDF, downloadBothPDF,
  buildQuoteHTML, buildReceiptHTML,
} from './utils/pdfGenerator';
import './App.css';

// ── 기본 회사 정보 ──────────────────────────────────────────
const DEFAULT_COMPANY: CompanyInfo = {
  name           : '주식회사속기법인지원',
  representative : '방지원',
  bizNumber      : '787-81-00770',
  address        : '서울특별시 서초구 서초대로42길 41',
  receiptAddress : '서울시 서초구 서초대로 42길 41, 7층',
  bizType        : '서비스',
  receiptBizType : '서비스업',
  bizItem        : '증거용녹취록제작',
  phone          : '02-3471-9999',
  fax            : '02-537-4787',
  sealImage      : undefined,
  sealWidth      : 48,
};

// ── 추천 품목 ────────────────────────────────────────────────
const SUGGESTED = [
  '녹취록', '전화녹취록', '대화 녹취록', '회의록',
  '출장속기', '주주총회 속기록', '증거용녹취록제작',
];

// ── 헬퍼 ────────────────────────────────────────────────────
function newItem(): InvoiceItem {
  return { id: Math.random().toString(36).slice(2), name: '', spec: '', qty: 1, unitPrice: 0, supplyAmount: 0, taxAmount: 0, note: '' };
}

function calcItem(item: InvoiceItem, tax: 'yes' | 'no'): InvoiceItem {
  const supply = Math.round(item.qty * item.unitPrice);
  const taxAmt = tax === 'yes' ? Math.round(supply * 0.1) : 0;
  return { ...item, supplyAmount: supply, taxAmount: taxAmt };
}

function blankForm(): QuoteData {
  return {
    docType: 'quote', quoteNo: '', receiptNo: '',
    clientName: '', clientPhone: '', clientEmail: '',
    issueDate: getTodayString(),
    taxInvoice: 'yes',
    items: [newItem()],
    deliveryDate: '', deliveryPlace: '', validDate: '',
    paymentTerms: '계좌이체',
    receiptType: 'receipt', paymentMethod: 'transfer', note: '',
  };
}

type TabType = 'quote' | 'receipt' | 'history' | 'settings';

// ════════════════════════════════════════════════════════════
export default function App() {
  const [tab,        setTab]        = useState<TabType>('quote');
  const [company,    setCompany]    = useState<CompanyInfo>(DEFAULT_COMPANY);
  const [form,       setForm]       = useState<QuoteData>(blankForm());
  const [history,    setHistory]    = useState<HistoryRecord[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [msg,        setMsg]        = useState('');
  const [showEmail,  setShowEmail]  = useState(false);
  const [showSms,    setShowSms]    = useState(false);
  const [emailData,  setEmailData]  = useState({ subject: '', body: '', to: '' });
  const [smsText,    setSmsText]    = useState('');
  const [prevTab,    setPrevTab]    = useState<'quote' | 'receipt'>('quote');
  const [hSearch,    setHSearch]    = useState({ name: '', date: '', docType: 'all', tax: 'all' });
  const [settings,   setSettings]   = useState<CompanyInfo>(DEFAULT_COMPANY);

  // 초기 로드
  useEffect(() => {
    const saved = loadSettings();
    if (saved) { setCompany(saved); setSettings(saved); }
    setHistory(loadHistory());
  }, []);

  // 최초 문서번호
  useEffect(() => {
    const today = getTodayString();
    setForm(f => ({
      ...f,
      quoteNo  : f.quoteNo   || generateDocNo('D', today),
      receiptNo: f.receiptNo || generateDocNo('R', today),
    }));
  // eslint-disable-next-line
  }, []);

  const toast = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 3200); };

  // ── 품목 조작 ──────────────────────────────────────────────
  const changeItem = (id: string, field: keyof InvoiceItem, val: any) =>
    setForm(prev => ({
      ...prev,
      items: prev.items.map(it =>
        it.id !== id ? it
          : calcItem({ ...it, [field]: (field === 'qty' || field === 'unitPrice') ? Number(val) : val }, prev.taxInvoice)
      ),
    }));

  const changeTax = (v: 'yes' | 'no') =>
    setForm(p => ({ ...p, taxInvoice: v, items: p.items.map(it => calcItem(it, v)) }));

  const addItemRow = () => setForm(p => ({ ...p, items: [...p.items, newItem()] }));

  const removeItemRow = (id: string) =>
    setForm(p => ({ ...p, items: p.items.length > 1 ? p.items.filter(i => i.id !== id) : p.items }));

  const addSuggested = (name: string) => {
    const item = calcItem({ ...newItem(), name }, form.taxInvoice);
    setForm(prev => {
      const last = prev.items[prev.items.length - 1];
      return last.name === ''
        ? { ...prev, items: [...prev.items.slice(0, -1), { ...last, ...item }] }
        : { ...prev, items: [...prev.items, item] };
    });
  };

  // ── 합계 계산 ──────────────────────────────────────────────
  const validItems   = form.items.filter(i => i.name.trim() !== '');
  const totalSupply  = validItems.reduce((s, i) => s + i.supplyAmount, 0);
  const totalTax     = form.taxInvoice === 'yes' ? validItems.reduce((s, i) => s + i.taxAmount, 0) : 0;
  const totalAmount  = totalSupply + totalTax;
  const canDownload  = form.clientName.trim() !== '';

  // ── 내역 저장 ─────────────────────────────────────────────
  const saveRecord = (docType?: QuoteData['docType']) => {
    const rec: HistoryRecord = {
      id          : `${Date.now()}`,
      docType     : docType || form.docType,
      clientName  : form.clientName,
      issueDate   : form.issueDate,
      quoteNo     : form.quoteNo,
      receiptNo   : form.receiptNo,
      taxInvoice  : form.taxInvoice,
      totalAmount,
      items       : form.items,
      clientPhone : form.clientPhone,
      clientEmail : form.clientEmail,
      note        : form.note,
      deliveryDate: form.deliveryDate,
      deliveryPlace: form.deliveryPlace,
      validDate   : form.validDate,
      paymentTerms: form.paymentTerms,
      receiptType : form.receiptType,
      paymentMethod: form.paymentMethod,
      savedAt     : new Date().toISOString(),
    };
    addRecord(rec);
    setHistory(loadHistory());
  };

  // ── PDF 다운로드 ──────────────────────────────────────────
  const dlQuote = async () => {
    if (!canDownload) { toast('의뢰인명을 입력해야 저장할 수 있습니다.'); return; }
    setLoading(true);
    try { await downloadQuotePDF(form, company); saveRecord('quote'); toast('견적서 PDF 저장 완료'); }
    catch { toast('PDF 생성 오류가 발생했습니다.'); }
    setLoading(false);
  };

  const dlReceipt = async () => {
    if (!canDownload) { toast('의뢰인명을 입력해야 저장할 수 있습니다.'); return; }
    setLoading(true);
    try { await downloadReceiptPDF(form, company); saveRecord('receipt'); toast('영수증 PDF 저장 완료'); }
    catch { toast('PDF 생성 오류가 발생했습니다.'); }
    setLoading(false);
  };

  const dlBoth = async () => {
    if (!canDownload) { toast('의뢰인명을 입력해야 저장할 수 있습니다.'); return; }
    setLoading(true);
    try { await downloadBothPDF(form, company); saveRecord('both'); toast('견적서+영수증 PDF 저장 완료'); }
    catch { toast('PDF 생성 오류가 발생했습니다.'); }
    setLoading(false);
  };

  // ── 견적서 → 영수증 연동 ──────────────────────────────────
  const convertToReceipt = () => {
    const today = getTodayString();
    setForm(f => ({
      ...f,
      docType  : 'receipt',
      receiptNo: f.receiptNo || generateDocNo('R', today),
    }));
    setTab('receipt');
    setPrevTab('receipt');
    toast('견적서 입력값으로 영수증이 자동 생성되었습니다. 확인 후 저장하세요.');
  };

  // ── 이메일 문구 생성 ─────────────────────────────────────
  const genEmail = () => {
    const docLabel = form.docType === 'receipt' ? '영수증' : '견적서';
    const bankLine = form.taxInvoice === 'yes'
      ? '신한 주식회사속기법인지원 140-011-879543'
      : '신한 방지원 110-364-065830';
    setEmailData({
      to     : form.clientEmail,
      subject: `[속기법인지원] ${form.clientName} ${docLabel} 보내드립니다.`,
      body   :
`안녕하세요.
속기법인지원입니다.

요청하신 ${docLabel} 전달드립니다.

합계금액 : ${formatNumber(totalAmount)}원
입금계좌 : ${bankLine}

확인 부탁드리며, 문의사항 있으시면 편하게 연락 주세요.

감사합니다.
속기법인지원 드림
${company.phone}`,
    });
    setShowEmail(true);
  };

  // ── 문자 문구 생성 ────────────────────────────────────────
  const genSms = () => {
    const docLabel = form.docType === 'receipt' ? '영수증' : '견적서';
    const bankLine = form.taxInvoice === 'yes'
      ? '신한 주식회사속기법인지원\n140-011-879543'
      : '신한 방지원\n110-364-065830';
    setSmsText(
`안녕하세요.
속기법인지원입니다.

요청하신 ${docLabel} 전달드립니다.

합계금액 : ${formatNumber(totalAmount)}원

입금계좌 :
${bankLine}

확인 부탁드립니다.
감사합니다.`
    );
    setShowSms(true);
  };

  // ── 클립보드 ──────────────────────────────────────────────
  const copy = (text: string, label: string) =>
    navigator.clipboard.writeText(text).then(() => toast(`${label} 복사 완료`));

  // ── 초기화 ───────────────────────────────────────────────
  const resetForm = () => {
    const today = getTodayString();
    setForm({ ...blankForm(), quoteNo: generateDocNo('D', today), receiptNo: generateDocNo('R', today) });
    setShowEmail(false); setShowSms(false);
    toast('입력값이 초기화되었습니다.');
  };

  // ── 내역 불러오기 ─────────────────────────────────────────
  const loadRec = (r: HistoryRecord) => {
    setForm({
      docType: r.docType, quoteNo: r.quoteNo, receiptNo: r.receiptNo,
      clientName: r.clientName, clientPhone: r.clientPhone, clientEmail: r.clientEmail,
      issueDate: r.issueDate, taxInvoice: r.taxInvoice, items: r.items,
      deliveryDate: r.deliveryDate, deliveryPlace: r.deliveryPlace,
      validDate: r.validDate, paymentTerms: r.paymentTerms,
      receiptType: r.receiptType, paymentMethod: r.paymentMethod, note: r.note,
    });
    setTab('quote'); toast('내역을 불러왔습니다. 수정 후 재발급하세요.');
  };

  // ── 설정 저장 ─────────────────────────────────────────────
  const saveSettingsFn = () => { setCompany(settings); saveSettings(settings); toast('설정이 저장되었습니다.'); };

  // 설정 도장 업로드 — canvas로 리사이즈 후 JPEG 압축 저장 (용량 절감)
  const handleSettingsSeal = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const img = new Image();
      img.onload = () => {
        const MAX = 200; // 최대 200px (도장은 작게 출력되므로 충분)
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const w = Math.round(img.width  * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width  = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, w, h);
        // PNG(투명배경) 유지: 도장은 배경 투명이 중요하므로 PNG로 저장
        const compressed = canvas.toDataURL('image/png');
        setSettings(s => ({ ...s, sealImage: compressed }));
      };
      img.src = ev.target?.result as string;
    };
    reader.readAsDataURL(file); e.target.value = '';
  };

  // ── 백업 ─────────────────────────────────────────────────
  const importBackupFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      if (importBackup(ev.target?.result as string)) { setHistory(loadHistory()); toast('백업 불러오기 완료'); }
      else toast('잘못된 백업 파일입니다.');
    };
    reader.readAsText(file); e.target.value = '';
  };

  // ── 내역 필터 ─────────────────────────────────────────────
  const filtered = history.filter(r => {
    if (hSearch.name && !r.clientName.includes(hSearch.name)) return false;
    if (hSearch.date && !r.issueDate.includes(hSearch.date)) return false;
    if (hSearch.docType !== 'all' && r.docType !== hSearch.docType) return false;
    if (hSearch.tax !== 'all' && r.taxInvoice !== hSearch.tax) return false;
    return true;
  });

  // ════════════════════════════════════════════════════════════
  // 렌더
  // ════════════════════════════════════════════════════════════
  return (
    <div className="app">

      {/* ── 헤더 ── */}
      <header className="app-header">
        <div className="header-brand">
          <span className="brand-logo">📄</span>
          <span className="brand-name">속기법인지원</span>
          <span className="brand-sub">견적서·영수증 자동화</span>
        </div>
        <nav className="header-nav">
          {(['quote','receipt','history','settings'] as TabType[]).map(t => (
            <button key={t}
              className={`nav-btn${tab === t ? ' active' : ''}`}
              onClick={() => setTab(t)}
            >
              {t==='quote'?'📋 견적서':t==='receipt'?'🧾 영수증':t==='history'?'📂 발급내역':'⚙️ 설정'}
            </button>
          ))}
        </nav>
      </header>

      {/* ── 토스트 ── */}
      {msg && <div className="msg-banner">{msg}</div>}

      {/* ── 작성 탭 ── */}
      {(tab === 'quote' || tab === 'receipt') && (
        <>
          <div className="main-layout">
            {/* 왼쪽 입력폼 */}
            <div className="form-panel">
              <InputForm
                form={form} setForm={setForm} activeTab={tab}
                changeItem={changeItem} changeTax={changeTax}
                addItemRow={addItemRow} removeItemRow={removeItemRow}
                addSuggested={addSuggested}
                totalSupply={totalSupply} totalTax={totalTax} totalAmount={totalAmount}
              />
            </div>
            {/* 오른쪽 미리보기 */}
            <div className="preview-panel">
              <div className="preview-tabs">
                {(['quote','receipt'] as const).map(t => (
                  <button key={t}
                    className={`preview-tab-btn${prevTab===t?' active':''}`}
                    onClick={() => setPrevTab(t)}
                  >{t==='quote'?'견적서 미리보기':'영수증 미리보기'}</button>
                ))}
              </div>
              <div className="preview-content">
                <iframe
                  srcDoc={prevTab==='quote' ? buildQuoteHTML(form, company) : buildReceiptHTML(form, company)}
                  title="미리보기" className="preview-iframe"
                />
              </div>
            </div>
          </div>

          {/* 하단 액션바 */}
          <div className="action-bar">
            <div className="action-group">
              <button className={`action-btn primary${!canDownload?' disabled':''}`}
                onClick={dlQuote} disabled={loading||!canDownload}
                title={!canDownload?'의뢰인명을 입력해야 합니다.':''}>
                {loading?'⏳':'📥'} 견적서 PDF
              </button>
              <button className={`action-btn primary${!canDownload?' disabled':''}`}
                onClick={dlReceipt} disabled={loading||!canDownload}>
                {loading?'⏳':'📥'} 영수증 PDF
              </button>
              <button className={`action-btn secondary${!canDownload?' disabled':''}`}
                onClick={dlBoth} disabled={loading||!canDownload}>
                📦 견적서+영수증
              </button>
            </div>

            {/* 견적서 → 영수증 연동 버튼 */}
            {tab === 'quote' && (
              <div className="action-group">
                <button className={`action-btn convert${!canDownload?' disabled':''}`}
                  onClick={convertToReceipt} disabled={!canDownload}
                  title="견적서 입력값을 영수증에 자동 복사">
                  ⚡ 영수증 자동생성
                </button>
              </div>
            )}

            <div className="action-group">
              <button className="action-btn info" onClick={genEmail}>✉️ 이메일 문구</button>
              <button className="action-btn info" onClick={genSms}>💬 문자 문구</button>
            </div>
            <div className="action-group">
              <button className="action-btn success" onClick={() => { saveRecord(); toast('저장되었습니다.'); }}>💾 저장</button>
              <button className="action-btn danger" onClick={resetForm}>🔄 초기화</button>
            </div>
            {!canDownload && <div className="warning-text">⚠️ 의뢰인명을 입력해야 저장할 수 있습니다.</div>}
          </div>
        </>
      )}

      {/* ── 이메일 모달 ── */}
      {showEmail && (
        <Modal title="✉️ 이메일 발송 문구" onClose={() => setShowEmail(false)}>
          <MsgField label="받는 사람 이메일">
            <CopyRow value={emailData.to} onCopy={() => copy(emailData.to,'이메일 주소')} />
          </MsgField>
          <MsgField label="이메일 제목">
            <CopyRow value={emailData.subject} onCopy={() => copy(emailData.subject,'이메일 제목')} />
          </MsgField>
          <MsgField label="이메일 본문">
            <div className="copy-col">
              <textarea readOnly value={emailData.body} className="copy-textarea" rows={9} />
              <button className="copy-btn" onClick={() => copy(emailData.body,'이메일 본문')}>본문 복사</button>
            </div>
          </MsgField>
        </Modal>
      )}

      {/* ── 문자 모달 ── */}
      {showSms && (
        <Modal title="💬 문자 발송 문구" onClose={() => setShowSms(false)}>
          <MsgField label="문자 내용">
            <div className="copy-col">
              <textarea readOnly value={smsText} className="copy-textarea" rows={12} />
              <button className="copy-btn" onClick={() => copy(smsText,'문자 내용')}>전체 복사</button>
            </div>
          </MsgField>
          {form.clientPhone && (
            <MsgField label="수신 번호">
              <CopyRow value={form.clientPhone} onCopy={() => copy(form.clientPhone,'전화번호')} />
            </MsgField>
          )}
        </Modal>
      )}

      {/* ── 발급 내역 탭 ── */}
      {tab === 'history' && (
        <HistoryPanel
          records={filtered} search={hSearch} setSearch={setHSearch}
          onLoad={loadRec}
          onDelete={id => { deleteRecord(id); setHistory(loadHistory()); toast('삭제되었습니다.'); }}
          onBackup={downloadBackup}
          onImport={importBackupFile}
          company={company} loading={loading}
          setLoading={setLoading} toast={toast}
        />
      )}

      {/* ── 설정 탭 ── */}
      {tab === 'settings' && (
        <SettingsPanel
          settings={settings} setSettings={setSettings}
          onSave={saveSettingsFn}
          onSealUpload={handleSettingsSeal}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// 입력폼
// ════════════════════════════════════════════════════════════
interface IFProps {
  form: QuoteData;
  setForm: React.Dispatch<React.SetStateAction<QuoteData>>;
  activeTab: TabType;
  changeItem: (id:string, f:keyof InvoiceItem, v:any)=>void;
  changeTax: (v:'yes'|'no')=>void;
  addItemRow: ()=>void;
  removeItemRow: (id:string)=>void;
  addSuggested: (name:string)=>void;
  totalSupply: number; totalTax: number; totalAmount: number;
}

function InputForm({ form, setForm, activeTab, changeItem, changeTax, addItemRow, removeItemRow, addSuggested, totalSupply, totalTax, totalAmount }: IFProps) {
  const f = (key: keyof QuoteData, val: any) => setForm(p => ({ ...p, [key]: val }));
  const taxLabel = form.taxInvoice === 'yes' ? '(부가세포함)' : '(부가세없음)';

  return (
    <div className="input-form">
      <h2 className="form-title">{activeTab==='quote'?'📋 견적서 작성':'🧾 영수증 작성'}</h2>

      {/* 기본 정보 */}
      <section className="form-section">
        <h3 className="section-title">기본 정보</h3>
        <div className="form-grid-2">
          <Field label={<>의뢰인명 <span className="req">*</span></>}>
            <input className="input" value={form.clientName} placeholder="예: 법무법인대륜"
              onChange={e => f('clientName', e.target.value)} />
          </Field>
          <Field label="연락처">
            <input className="input" value={form.clientPhone} placeholder="010-0000-0000"
              onChange={e => f('clientPhone', e.target.value)} />
          </Field>
          <Field label="이메일">
            <input className="input" type="email" value={form.clientEmail} placeholder="example@email.com"
              onChange={e => f('clientEmail', e.target.value)} />
          </Field>
          <Field label="발행일자">
            <input className="input" type="date" value={form.issueDate}
              onChange={e => f('issueDate', e.target.value)} />
          </Field>
          <Field label="견적서 번호">
            <input className="input" value={form.quoteNo} onChange={e => f('quoteNo', e.target.value)} />
          </Field>
          <Field label="영수증 번호">
            <input className="input" value={form.receiptNo} onChange={e => f('receiptNo', e.target.value)} />
          </Field>
        </div>
      </section>

      {/* 세금계산서 */}
      <section className="form-section">
        <h3 className="section-title">세금계산서 / 결제 옵션</h3>
        <div className="form-grid-2">
          <Field label="세금계산서">
            <div className="radio-group">
              {(['yes','no'] as const).map(v => (
                <label key={v} className={`radio-label${form.taxInvoice===v?' selected':''}`}>
                  <input type="radio" checked={form.taxInvoice===v} onChange={() => changeTax(v)} />
                  {v==='yes'?'세금계산서 발행':'세금계산서 미발행'}
                </label>
              ))}
            </div>
          </Field>
          <Field label="영수/청구">
            <div className="radio-group">
              {(['receipt','claim'] as const).map(v => (
                <label key={v} className={`radio-label${form.receiptType===v?' selected':''}`}>
                  <input type="radio" checked={form.receiptType===v} onChange={() => f('receiptType',v)} />
                  {v==='receipt'?'영수':'청구'}
                </label>
              ))}
            </div>
          </Field>
          <Field label="결제수단">
            <select className="input select" value={form.paymentMethod}
              onChange={e => f('paymentMethod', e.target.value)}>
              <option value="transfer">계좌이체</option>
              <option value="card">카드</option>
              <option value="cash">현금</option>
            </select>
          </Field>
        </div>
      </section>

      {/* 품목 */}
      <section className="form-section">
        <h3 className="section-title">품목 리스트</h3>
        <div className="suggested-items">
          <span className="suggested-label">빠른 추가:</span>
          {SUGGESTED.map(n => (
            <button key={n} className="tag-btn" onClick={() => addSuggested(n)}>{n}</button>
          ))}
        </div>
        <div className="items-wrapper">
          <table className="items-input-table">
            <thead>
              <tr>
                <th>품목명</th><th style={{width:66}}>규격</th><th style={{width:48}}>수량</th>
                <th style={{width:98}}>단가</th><th style={{width:98}}>공급가액</th>
                <th style={{width:78}}>세액</th><th style={{width:70}}>비고</th>
                <th style={{width:34}}></th>
              </tr>
            </thead>
            <tbody>
              {form.items.map(it => (
                <tr key={it.id}>
                  <td><input className="table-input" value={it.name} placeholder="품목명"
                    onChange={e => changeItem(it.id,'name',e.target.value)} /></td>
                  <td><input className="table-input" value={it.spec}
                    onChange={e => changeItem(it.id,'spec',e.target.value)} /></td>
                  <td><input className="table-input text-right" type="number" value={it.qty||''}
                    onChange={e => changeItem(it.id,'qty',e.target.value)} /></td>
                  <td><input className="table-input text-right" type="number" value={it.unitPrice||''}
                    onChange={e => changeItem(it.id,'unitPrice',e.target.value)} /></td>
                  <td><div className="table-readonly">{formatNumber(it.supplyAmount)}</div></td>
                  <td><div className="table-readonly">{form.taxInvoice==='yes'?formatNumber(it.taxAmount):'-'}</div></td>
                  <td><input className="table-input" value={it.note}
                    onChange={e => changeItem(it.id,'note',e.target.value)} /></td>
                  <td><button className="del-btn" onClick={() => removeItemRow(it.id)}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button className="add-item-btn" onClick={addItemRow}>+ 품목 추가</button>
        <div className="total-summary">
          <span>공급가액: <b>{formatNumber(totalSupply)}</b></span>
          {form.taxInvoice==='yes' && <span>세액: <b>{formatNumber(totalTax)}</b></span>}
          <span className="total-amount">합계: <b>₩ {formatNumber(totalAmount)}</b> {taxLabel}</span>
        </div>
      </section>

      {/* 납기 등 */}
      <section className="form-section">
        <h3 className="section-title">추가 정보 (견적서)</h3>
        <div className="form-grid-2">
          <Field label="납기일자"><input className="input" value={form.deliveryDate} placeholder="협의"
            onChange={e => f('deliveryDate',e.target.value)} /></Field>
          <Field label="납품장소"><input className="input" value={form.deliveryPlace} placeholder="의뢰인 사무소"
            onChange={e => f('deliveryPlace',e.target.value)} /></Field>
          <Field label="유효일자"><input className="input" value={form.validDate} placeholder="30일"
            onChange={e => f('validDate',e.target.value)} /></Field>
          <Field label="결제조건"><input className="input" value={form.paymentTerms}
            onChange={e => f('paymentTerms',e.target.value)} /></Field>
        </div>
      </section>

      {/* 비고 */}
      <section className="form-section">
        <h3 className="section-title">비고</h3>
        <textarea className="input textarea" rows={3} value={form.note} placeholder="추가 메모"
          onChange={e => f('note',e.target.value)} />
      </section>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// 발급 내역 패널
// ════════════════════════════════════════════════════════════
interface HPProps {
  records: HistoryRecord[]; search: any; setSearch: any;
  onLoad: (r:HistoryRecord)=>void; onDelete: (id:string)=>void;
  onBackup: ()=>void; onImport: (e:React.ChangeEvent<HTMLInputElement>)=>void;
  company: CompanyInfo; loading: boolean;
  setLoading: (v:boolean)=>void; toast: (m:string)=>void;
}
function HistoryPanel({ records, search, setSearch, onLoad, onDelete, onBackup, onImport, company, loading, setLoading, toast }: HPProps) {
  const DL = async (r: HistoryRecord) => {
    setLoading(true);
    const fd: QuoteData = {
      docType: r.docType, quoteNo: r.quoteNo, receiptNo: r.receiptNo,
      clientName: r.clientName, clientPhone: r.clientPhone, clientEmail: r.clientEmail,
      issueDate: r.issueDate, taxInvoice: r.taxInvoice, items: r.items,
      deliveryDate: r.deliveryDate, deliveryPlace: r.deliveryPlace,
      validDate: r.validDate, paymentTerms: r.paymentTerms,
      receiptType: r.receiptType, paymentMethod: r.paymentMethod, note: r.note,
    };
    try {
      if (r.docType==='receipt') await downloadReceiptPDF(fd, company);
      else if (r.docType==='both') await downloadBothPDF(fd, company);
      else await downloadQuotePDF(fd, company);
      toast('PDF 다운로드 완료');
    } catch { toast('PDF 오류'); }
    setLoading(false);
  };

  const DL_label = (t: string) => t==='quote'?'견적서':t==='receipt'?'영수증':'견적+영수';

  return (
    <div className="history-panel">
      <div className="history-header">
        <h2>📂 발급 내역</h2>
        <div className="history-actions">
          <button className="btn-sm" onClick={onBackup}>💾 JSON 백업</button>
          <label className="btn-sm" style={{cursor:'pointer'}}>
            📂 백업 불러오기
            <input type="file" accept=".json" style={{display:'none'}} onChange={onImport} />
          </label>
        </div>
      </div>
      <div className="history-filters">
        <input className="filter-input" placeholder="의뢰인명 검색"
          value={search.name} onChange={e => setSearch((s:any)=>({...s,name:e.target.value}))} />
        <input className="filter-input" type="date" value={search.date}
          onChange={e => setSearch((s:any)=>({...s,date:e.target.value}))} />
        <select className="filter-select" value={search.docType}
          onChange={e => setSearch((s:any)=>({...s,docType:e.target.value}))}>
          <option value="all">전체 문서</option>
          <option value="quote">견적서</option>
          <option value="receipt">영수증</option>
          <option value="both">동시 발급</option>
        </select>
        <select className="filter-select" value={search.tax}
          onChange={e => setSearch((s:any)=>({...s,tax:e.target.value}))}>
          <option value="all">세금계산서 전체</option>
          <option value="yes">발행</option>
          <option value="no">미발행</option>
        </select>
        <button className="btn-sm" onClick={() => setSearch({name:'',date:'',docType:'all',tax:'all'})}>초기화</button>
      </div>
      <div className="history-count">총 {records.length}건</div>
      {records.length === 0 ? (
        <div className="empty-history">발급 내역이 없습니다.</div>
      ) : (
        <div className="history-table-wrapper">
          <table className="history-table">
            <thead>
              <tr>
                <th>발행일자</th><th>의뢰인명</th><th>문서종류</th>
                <th>문서번호</th><th>세금계산서</th><th>합계금액</th>
                <th>저장일시</th><th>관리</th>
              </tr>
            </thead>
            <tbody>
              {records.map(r => (
                <tr key={r.id}>
                  <td>{r.issueDate}</td>
                  <td><b>{r.clientName}</b></td>
                  <td><span className={`badge ${r.docType}`}>{DL_label(r.docType)}</span></td>
                  <td style={{fontSize:'0.78rem'}}>
                    {r.quoteNo && <div>{r.quoteNo}</div>}
                    {r.receiptNo && r.docType!=='quote' && <div>{r.receiptNo}</div>}
                  </td>
                  <td><span className={`badge ${r.taxInvoice==='yes'?'tax-yes':'tax-no'}`}>
                    {r.taxInvoice==='yes'?'발행':'미발행'}
                  </span></td>
                  <td style={{textAlign:'right'}}>{formatNumber(r.totalAmount)}원</td>
                  <td style={{fontSize:'0.75rem'}}>{r.savedAt.slice(0,16).replace('T',' ')}</td>
                  <td>
                    <div className="history-btns">
                      <button className="btn-xs primary" onClick={() => onLoad(r)}>불러오기</button>
                      <button className="btn-xs secondary" onClick={() => DL(r)} disabled={loading}>
                        {loading?'…':'PDF'}
                      </button>
                      <button className="btn-xs danger"
                        onClick={() => window.confirm(`"${r.clientName}" 삭제?`) && onDelete(r.id)}>삭제</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// 설정 패널
// ════════════════════════════════════════════════════════════
function SettingsPanel({ settings, setSettings, onSave, onSealUpload }:{
  settings: CompanyInfo;
  setSettings: React.Dispatch<React.SetStateAction<CompanyInfo>>;
  onSave: ()=>void;
  onSealUpload: (e:React.ChangeEvent<HTMLInputElement>)=>void;
}) {
  const FIELDS: {key: keyof CompanyInfo; label: string}[] = [
    {key:'name',          label:'상호'},
    {key:'representative',label:'대표자'},
    {key:'bizNumber',     label:'사업자등록번호'},
    {key:'address',       label:'주소 (견적서용)'},
    {key:'receiptAddress',label:'주소 (영수증용)'},
    {key:'bizType',       label:'업태 (견적서용)'},
    {key:'receiptBizType',label:'업태 (영수증용)'},
    {key:'bizItem',       label:'종목'},
    {key:'phone',         label:'전화번호'},
    {key:'fax',           label:'팩스번호'},
  ];

  return (
    <div className="settings-panel">
      <h2>⚙️ 회사 정보 설정</h2>
      <p className="settings-desc">수정 후 저장하면 모든 문서에 자동 반영됩니다.</p>
      <div className="settings-form">
        {FIELDS.map(({key, label}) => (
          <div className="form-field" key={key}>
            <label>{label}</label>
            <input className="input" value={(settings[key] as string)||''}
              onChange={e => setSettings(s => ({...s, [key]: e.target.value}))} />
          </div>
        ))}

        {/* 도장 업로드 */}
        <div className="form-field" style={{marginTop:12}}>
          <label style={{fontWeight:700, color:'#1a237e'}}>🔖 도장 이미지 (설정 저장 시 전체 문서 자동 적용)</label>
          <p style={{fontSize:'0.8rem', color:'#666', marginBottom:6}}>
            PNG 투명배경 권장. 저장하면 견적서·영수증에 자동 적용됩니다.
          </p>
          <input type="file" accept="image/png,image/jpeg" onChange={onSealUpload} className="input" />
          {settings.sealImage && (
            <div className="seal-preview-box">
              <img src={settings.sealImage} alt="도장 미리보기"
                style={{width: settings.sealWidth||48, border:'1px dashed #aaa', background:'#f9f9f9'}} />
              <div style={{display:'flex', gap:8, alignItems:'center', marginTop:6}}>
                <label style={{fontSize:'0.8rem'}}>크기(px):</label>
                <input type="number" min={20} max={200} className="input"
                  style={{width:80}} value={settings.sealWidth||48}
                  onChange={e => setSettings(s => ({...s, sealWidth: Number(e.target.value)}))} />
                <button className="danger-btn"
                  onClick={() => setSettings(s => ({...s, sealImage: undefined}))}>도장 제거</button>
              </div>
            </div>
          )}
        </div>

        <button className="save-btn" onClick={onSave}>💾 설정 저장</button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// 공용 소형 컴포넌트
// ════════════════════════════════════════════════════════════
function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="form-field">
      <label>{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, children, onClose }: { title:string; children:React.ReactNode; onClose:()=>void }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

function MsgField({ label, children }: { label:string; children:React.ReactNode }) {
  return (
    <div className="msg-field">
      <label>{label}</label>
      {children}
    </div>
  );
}

function CopyRow({ value, onCopy }: { value:string; onCopy:()=>void }) {
  return (
    <div className="copy-row">
      <input readOnly value={value} className="copy-input" />
      <button className="copy-btn" onClick={onCopy}>복사</button>
    </div>
  );
}
